<template>
  <a id="pdpRelatedProducts" />
  <div
    ref="intersectionTarget"
    class="h-px w-full"
    aria-hidden="true"
  />
  <div v-for="(list, listindex) in relatedProductsLists" :key="list.title + listindex">
    <div class="pb-24 flex-1 relative">
      <h3 class="type-headline-xl mb-24 mobOnly:px-12">{{ list.title }}</h3>
      <div class="transition-opacity duration-300 opacity-100 desk:layout-container mobOnly:mt-24">
        <div v-if="list.products.length > 5" class="hidden desk:flex justify-between">
          <div class="swiper-button-prev swiper-for-blok-recommended absolute inset-y-0 !top-[45%]" />
          <div class="swiper-button-next swiper-for-blok-recommended absolute inset-y-0 !top-[45%]" />
        </div>
        <client-only>
          <Swiper
            :space-between="uiStore.isMobile ? 2 : 8"
            :slides-per-view="uiStore.isMobile ? 2.5 : 5"
            :slides-offset-before="uiStore.isMobile ? 12 : 0"
            :slides-offset-after="uiStore.isMobile ? 24 : 0"
            :modules="[SwiperNavigation, SwiperPagination]"
            :navigation="{
              nextEl: `.swiper-button-next.swiper-for-blok-recommended`,
              prevEl: `.swiper-button-prev.swiper-for-blok-recommended`,
            }"
            :pagination="{
              clickable: true,
              dynamicBullets: true,
            }"
            class="mobOnly:px-24"
          >
            <SwiperSlide
              v-for="(product, slideIndex) in list.products"
              :key="product.partNo"
            >
              <ProductCard
                :key="product.partNo + slideIndex"
                v-track="{product, index: slideIndex, pageName: globalContent.currentStory?.name, pageId: globalContent.currentStory?.id + 1}"
                :product="product"
                :enable-quick-buy="true"
                :point-shop="false"
                :placement="100"
                design="small"
              />
            </SwiperSlide>
          </Swiper>
        </client-only>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useUiStore } from '~/store/ui';
import { useGlobalContentStore } from '~/store/globalContent';
import ProductCard from '~/components/product/ProductCard.vue';
import { ProductModel } from '~/models/product';
import useStructuredData from '~/composeables/useStructuredData';
import { useProductImpressionTracking as vTrack } from '~/composeables/useTracking';
import { NorceRelatedProductTypeCodes } from '~/constants/norceCodes';
import { useProductStore } from '~/store/product';

const globalContent = useGlobalContentStore();
const uiStore = useUiStore();
const productStore = useProductStore();

const { $t } = useNuxtApp();

const props = defineProps<{
  productItem: ProductModel,
}>();

type RelatedProductLists = {
  title: string,
  products: ProductModel[]
}

const relatedProductsLists = ref<RelatedProductLists[]>([]);
const intersectionTarget = ref<HTMLElement | null>(null);
const hasLoaded = ref(false);
let observer: IntersectionObserver | null = null;

// Can add mor categories if we want
const loadRelations = [NorceRelatedProductTypeCodes.Related];

const loadRelatedProducts = async() => {
  if (hasLoaded.value) {
    return;
  }
  hasLoaded.value = true;

  for (const relation of loadRelations) {
    const partNos = props.productItem.relatedProductsByType(relation);
    if (partNos.length > 0) {
      const allProducts = await productStore.getRelatedProducts(partNos);
      // Filter out of stock products - only apply this filter if category != resilient
      const filteredProducts = allProducts.filter(product => {
        const isResilient = product.categories.some(f => f.code === 'Resilient' || f.code === 'ResilientAccessories');
        if (isResilient) {
          return true;
        }
        return product.variants.some(variant => variant.isInStock);
      });

      if (filteredProducts.length > 0) {
        relatedProductsLists.value.push({
          title: props.productItem.extendedLayout ? $t('productPage.relatedProducts.addOns') : $t('productPage.relatedProducts.' + relation.replace('-', '')),
          products: filteredProducts,
        });
      }
    }
  }

  if (props.productItem.extendedLayout) {
    const allBrandProducts = await productStore.getProductBrandRelatedProducts(props.productItem);
    // Filter out of stock products - only apply this filter if category != resilient
    const filteredBrandProducts = allBrandProducts.filter(product => {
      const isResilient = product.categories.some(f => f.code === 'Resilient' || f.code === 'ResilientAccessories');
      if (isResilient) {
        return true;
      }
      return product.variants.some(variant => variant.isInStock);
    });

    if (filteredBrandProducts.length > 0) {
      relatedProductsLists.value.push({
        title: $t('productPage.relatedProducts.fromBrand', { brand : props.productItem.brand.name }),
        products: filteredBrandProducts,
      });
    }
  }
};

onMounted(() => {
  if (!('IntersectionObserver' in window)) {
    loadRelatedProducts();
    return;
  }

  observer = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      loadRelatedProducts();
      observer?.disconnect();
      observer = null;
    }
  }, {
    root: null,
    threshold: 0.1,
  });

  if (intersectionTarget.value) {
    observer.observe(intersectionTarget.value);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
});

const { buildItemListJsonLd } = useStructuredData();
const allRelatedProducts = computed(() => relatedProductsLists.value.flatMap(list => list.products));
useJsonld(() => buildItemListJsonLd(allRelatedProducts.value, 'RelatedProductsList'));
</script>

<style scoped lang="postcss">
.swiper-button-prev {
  left: -15px;
}
.swiper-button-next {
  right: -15px;
}
.swiper-button-disabled {
  display: none;
}
</style>
